import { useEffect, useRef, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { API_URL as API } from '../config/env.js';
import { shareSiteOnWhatsApp, trackEvent } from '../utils/analytics';
import './ReferralPopup.css';

function ProfileIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" strokeLinecap="round" />
    </svg>
  );
}

export default function ReferralPopup() {
  const { t } = useLang();
  const { user, syncUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [claimCode, setClaimCode] = useState('');
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!user?.token) {
      setInfo(null);
      return;
    }
    fetch(`${API}/api/referrals/me`, {
      headers: { Authorization: `Bearer ${user.token}` },
    })
      .then((r) => r.json())
      .then(setInfo)
      .catch(() => {});
  }, [user?.token]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const shareUrl = info?.shareUrl || '';
  const trialActive = Boolean(user?.trialUntil && Number(user.trialUntil) > Date.now());
  const showClaimForm = Boolean(user?.token && info && !info.referred);

  async function copyLink() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  function shareReferral() {
    if (!shareUrl) return;
    trackEvent('share_referral', { method: 'whatsapp' });
    shareSiteOnWhatsApp(`${t.referralShareText}\n${t.referralShareCode}: ${info?.code}`, shareUrl);
  }

  async function claimReferral(e) {
    e.preventDefault();
    if (!claimCode.trim() || !user?.token) return;
    setNote('');
    try {
      const res = await fetch(`${API}/api/referrals/claim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.token}`,
        },
        body: JSON.stringify({ code: claimCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t.referralClaimFail);
      if (data.applied) {
        syncUser({
          isSubscribed: true,
          isTrial: true,
          trialUntil: data.trialUntil,
          subscribedUntil: data.trialUntil,
        });
        setNote(t.referralClaimSuccess);
      } else {
        setNote(t.referralClaimFail);
      }
    } catch (ex) {
      setNote(ex.message || t.referralClaimFail);
    }
  }

  function openPopup() {
    trackEvent('referral_popup_open', { placement: 'profile_fab' });
    setOpen(true);
  }

  return (
    <>
      <button
        type="button"
        className="ref-fab"
        onClick={openPopup}
        aria-label={t.referralTitle}
        title={t.referralTitle}
        data-sub-free
      >
        <span className="ref-fab__icon">
          <ProfileIcon />
        </span>
        <span className="ref-fab__label">{t.referralFabLabel}</span>
      </button>

      {open && (
        <div className="ref-modal" role="dialog" aria-modal="true" aria-labelledby="ref-modal-title">
          <button
            type="button"
            className="ref-modal__backdrop"
            aria-label={t.closeBtn}
            onClick={() => setOpen(false)}
          />
          <div className="ref-modal__panel" ref={panelRef} tabIndex={-1}>
            <button
              type="button"
              className="ref-modal__close"
              onClick={() => setOpen(false)}
              aria-label={t.closeBtn}
            >
              ✕
            </button>

            <div className="ref-modal__brand">
              <div className="ref-modal__logo-ring">
                <img src="/rk-logo.webp" alt={t.brandName} />
              </div>
            </div>

            <h2 id="ref-modal-title" className="ref-modal__title">
              {t.referralTitle}
            </h2>
            <p className="ref-modal__lead">{t.referralLead}</p>

            {!user?.token ? (
              <p className="ref-modal__login">
                <a href="/login">{t.signInLabel}</a>
              </p>
            ) : null}

            {info?.code ? (
              <div className="ref-modal__code-row">
                <span className="ref-modal__code">{info.code}</span>
                <button type="button" className="ref-modal__btn" onClick={copyLink}>
                  {copied ? t.referralCopied : t.referralCopy}
                </button>
                <button type="button" className="ref-modal__btn ref-modal__btn--primary" onClick={shareReferral}>
                  {t.referralShareWa}
                </button>
              </div>
            ) : null}

            {trialActive ? (
              <p className="ref-modal__note">
                {t.referralTrialActive.replace(
                  '{date}',
                  new Date(Number(user.trialUntil)).toLocaleDateString()
                )}
              </p>
            ) : null}

            {showClaimForm ? (
              <form className="ref-modal__claim" onSubmit={claimReferral}>
                <input
                  type="text"
                  className="ref-modal__input"
                  placeholder={t.referralClaimPlaceholder}
                  value={claimCode}
                  onChange={(ev) => setClaimCode(ev.target.value.toUpperCase())}
                  maxLength={12}
                />
                <button type="submit" className="ref-modal__btn ref-modal__btn--primary">
                  {t.referralClaimBtn}
                </button>
              </form>
            ) : null}

            {note ? <p className="ref-modal__note">{note}</p> : null}
          </div>
        </div>
      )}
    </>
  );
}
